import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Avatar,
  Rating,
  useTheme,
  Chip,
  IconButton,
  MobileStepper,
  Button,
  useMediaQuery,
} from '@mui/material';
import {
  FormatQuote as QuoteIcon,
  Apartment as ApartmentIcon,
  House as HouseIcon,
  Business as BusinessIcon,
  LocationCity as LocationCityIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';

// Componente estilizado para o card de depoimento
const TestimonialCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 16,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  boxShadow: '0 8px 20px rgba(0, 0, 0, 0.05)',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 28px rgba(0, 0, 0, 0.1)',
  },
}));

// Interface para os depoimentos
interface TestimonialProps {
  id: number;
  name: string;
  role: string;
  company: string;
  avatar: string;
  rating: number;
  testimonial: string;
  plan: 'Intermediário' | 'Premium';
  segment: 'Imobiliária' | 'Corretor' | 'Construtora' | 'Incorporadora';
}

// Componente de depoimentos
const Testimonials: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Lista de depoimentos
  const testimonials: TestimonialProps[] = [
    {
      id: 1,
      name: 'Ana Silva',
      role: 'Corretora Autônoma',
      company: 'Silva Imóveis',
      avatar: '/assets/avatars/avatar-1.jpg',
      rating: 5,
      testimonial: 'O WhatsApp IA Bot transformou meu negócio! Consigo atender clientes 24h por dia e minha taxa de conversão aumentou em 40%. O plano Intermediário atende perfeitamente minhas necessidades como corretora autônoma.',
      plan: 'Intermediário',
      segment: 'Corretor'
    },
    {
      id: 2,
      name: 'Carlos Mendes',
      role: 'Diretor Comercial',
      company: 'Mendes Incorporações',
      avatar: '/assets/avatars/avatar-2.jpg',
      rating: 5,
      testimonial: 'Como incorporadora, precisávamos de uma solução completa. O plano Premium nos deu acesso ilimitado a todas as funcionalidades, especialmente o assistente de e-mail com IA e a gestão de redes sociais, que são essenciais para nosso marketing digital.',
      plan: 'Premium',
      segment: 'Incorporadora'
    },
    {
      id: 3,
      name: 'Juliana Costa',
      role: 'Gerente de Marketing',
      company: 'Costa Imobiliária',
      avatar: '/assets/avatars/avatar-3.jpg',
      rating: 4,
      testimonial: 'A gestão de redes sociais com IA nos ajudou a criar conteúdo relevante e aumentar nosso engajamento. O calendário de conteúdo e as métricas detalhadas são excelentes para planejar nossas estratégias de marketing.',
      plan: 'Premium',
      segment: 'Imobiliária'
    },
    {
      id: 4,
      name: 'Roberto Almeida',
      role: 'CEO',
      company: 'Almeida Construções',
      avatar: '/assets/avatars/avatar-4.jpg',
      rating: 5,
      testimonial: 'Nossa construtora conseguiu automatizar o atendimento inicial e qualificar leads com muito mais eficiência. O sistema de base de conhecimento (RAG) permite que o bot responda perguntas específicas sobre nossos empreendimentos.',
      plan: 'Premium',
      segment: 'Construtora'
    },
    {
      id: 5,
      name: 'Fernanda Oliveira',
      role: 'Corretora',
      company: 'Oliveira Imóveis',
      avatar: '/assets/avatars/avatar-5.jpg',
      rating: 4,
      testimonial: 'Comecei com o plano gratuito e logo fiz upgrade para o Intermediário. O retorno sobre o investimento foi imediato! A captura automática de leads do WhatsApp e a classificação de e-mails economizam muito do meu tempo.',
      plan: 'Intermediário',
      segment: 'Corretor'
    },
  ];

  const [activeStep, setActiveStep] = useState(0);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStepChange = (step: number) => {
    setActiveStep(step);
  };

  // Função para renderizar o ícone do segmento
  const renderSegmentIcon = (segment: string) => {
    switch (segment) {
      case 'Imobiliária':
        return <ApartmentIcon fontSize="small" />;
      case 'Corretor':
        return <HouseIcon fontSize="small" />;
      case 'Construtora':
        return <BusinessIcon fontSize="small" />;
      case 'Incorporadora':
        return <LocationCityIcon fontSize="small" />;
      default:
        return <ApartmentIcon fontSize="small" />;
    }
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        borderRadius: '16px', 
        mb: 4,
        background: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(0, 0, 0, 0.05)',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.05)',
        overflow: 'hidden',
        position: 'relative',
        p: 3
      }}
    >
      <Box sx={{ 
        position: 'absolute', 
        top: 0, 
        left: 0, 
        right: 0, 
        height: '5px', 
        background: 'linear-gradient(90deg, #3f51b5, #f50057)'
      }} />
      
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          O que nossos clientes dizem
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '800px', mx: 'auto' }}>
          Veja como o Vendedor Virtual está transformando negócios imobiliários em todo o Brasil
        </Typography>
      </Box>
      
      <Box sx={{ px: { xs: 0, md: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <MobileStepper
            steps={testimonials.length}
            position="static"
            variant="text"
            activeStep={activeStep}
            nextButton={
              <Button size="small" onClick={handleNext} disabled={activeStep === testimonials.length - 1}>
                Próximo
                <KeyboardArrowRight />
              </Button>
            }
            backButton={
              <Button size="small" onClick={handleBack} disabled={activeStep === 0}>
                <KeyboardArrowLeft />
                Anterior
              </Button>
            }
          />
        </Box>
        
        {testimonials.map((testimonial, index) => (
          <Box key={testimonial.id} sx={{ display: index === activeStep ? 'block' : 'none' }}>
            <TestimonialCard>
              <Box sx={{ 
                position: 'absolute', 
                top: 16, 
                right: 16, 
                bgcolor: testimonial.plan === 'Premium' ? theme.palette.secondary.main : theme.palette.primary.main,
                color: 'white',
                px: 1.5,
                py: 0.5,
                borderRadius: '12px',
                fontSize: '0.75rem',
                fontWeight: 'bold'
              }}>
                Plano {testimonial.plan}
              </Box>
              
              <IconButton 
                sx={{ 
                  position: 'absolute', 
                  top: 12, 
                  left: 12, 
                  color: 'rgba(0, 0, 0, 0.1)',
                  p: 0
                }}
                disableRipple
              >
                <QuoteIcon fontSize="large" />
              </IconButton>
              
              <Box sx={{ mb: 2, mt: 3 }}>
                <Rating value={testimonial.rating} readOnly precision={0.5} />
              </Box>
              
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 3, 
                  fontStyle: 'italic',
                  color: 'text.primary',
                  flexGrow: 1,
                  minHeight: '120px'
                }}
              >
                "{testimonial.testimonial}"
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 'auto' }}>
                <Avatar 
                  src={testimonial.avatar} 
                  alt={testimonial.name}
                  sx={{ 
                    width: 50, 
                    height: 50,
                    border: '2px solid',
                    borderColor: testimonial.plan === 'Premium' ? theme.palette.secondary.main : theme.palette.primary.main
                  }}
                />
                <Box sx={{ ml: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {testimonial.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {testimonial.role}, {testimonial.company}
                  </Typography>
                  <Chip 
                    icon={renderSegmentIcon(testimonial.segment)} 
                    label={testimonial.segment}
                    size="small"
                    sx={{ 
                      mt: 0.5, 
                      height: 22, 
                      fontSize: '0.7rem',
                      bgcolor: 'rgba(0, 0, 0, 0.05)'
                    }}
                  />
                </Box>
              </Box>
            </TestimonialCard>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default Testimonials;
